import React, { useState } from 'react';
import PropTypes from 'prop-types';
import generalStyles from '../GeneralStyles.module.scss';
import { connect } from 'react-redux';
import { postGoalSetting } from '../../../actions/new-onboarding';

const GoalSetting = ({ initialInformation, isInvalidToken, moveToNextStep, postGoalSetting }) => {
  const [firstField, setFirstField] = useState('');
  const [secondField, setSecondField] = useState('');
  const [showMoreFirst, setShowMoreFirst] = useState(false);
  const [showMoreSecond, setShowMoreSecond] = useState(false);

  const firstFieldSuggestions = [
    'give a presentation at work',
    'travel without needing translation help',
    'take part in meetings more actively',
    'write better emails to clients',
    'understand fast conversations on TV or radio',
    'interview for jobs in ' + initialInformation?.language,
    'have confident conversations with locals',
    'lead meetings in ' + initialInformation?.language,
    'make new friends who speak ' + initialInformation?.language,
    'handle customer support calls smoothly'
  ];

  const secondFieldSuggestions = [
    'get promoted at work',
    'relocate to another country',
    'apply for international roles',
    'feel more confident in my job',
    'live in another country without language barriers',
    'connect with my in-laws and their culture',
    'travel independently',
    'pass a language certification',
    'feel more at home in a new country',
    'socialize with my team more naturally'
  ];

  const NUM_VISIBLE_SUGGESTIONS = 3;

  const handleSubmit = () => {
    if (!firstField.trim() || !secondField.trim()) {
      return;
    }

    const goalData = {
      action: firstField.trim(),
      outcome: secondField.trim(),
      fullGoal: `By the end of my ${
        initialInformation?.language
      } course, I want to be able to ${firstField.trim()} so that I can ${secondField.trim()}.`
    };

    const securityParams = {
      email: initialInformation?.email,
      invitationToken: initialInformation?.invitationToken
    };

    // Save goal data in parallel, don't wait for response
    postGoalSetting({ goalData }, securityParams);

    window.scrollTo(0, 0);

    // Move to next step immediately
    moveToNextStep();
  };

  const canProceed = firstField.trim() && secondField.trim();

  const SuggestionChips = ({ suggestions, onSelect, showMore, onShowMore }) => {
    const visibleSuggestions = showMore ? suggestions : suggestions.slice(0, NUM_VISIBLE_SUGGESTIONS);

    return (
      <div className="suggestions-container">
        <div className="chips-grid">
          {visibleSuggestions.map((suggestion, index) => (
            <button key={index} className="suggestion-chip" onClick={() => onSelect(suggestion)} disabled={isInvalidToken}>
              {suggestion}
            </button>
          ))}
        </div>
        {!showMore && suggestions.length > NUM_VISIBLE_SUGGESTIONS && (
          <button className="show-more-btn" onClick={onShowMore} disabled={isInvalidToken}>
            + {suggestions.length - NUM_VISIBLE_SUGGESTIONS} more
          </button>
        )}
      </div>
    );
  };

  SuggestionChips.propTypes = {
    suggestions: PropTypes.array.isRequired,
    onSelect: PropTypes.func.isRequired,
    showMore: PropTypes.bool.isRequired,
    onShowMore: PropTypes.func.isRequired
  };

  return (
    <div className={generalStyles['content-wrapper']}>
      <h1 className={generalStyles['title']} id="tell-us-why-title">
        What is your learning goal, {initialInformation?.firstName}?
      </h1>
      <p className="subtitle">
        Setting a clear and specific goal can improve your performance by up to 25%. Tell us what you would like to achieve by the end of your{' '}
        {initialInformation?.language} course, and we will support you every step of the way.
      </p>

      <div className="goal-builder">
        <div className="goal-sentence">
          <span className="sentence-text">By the end of my </span>
          <span className="language-highlight">{initialInformation?.language}</span>
          <span className="sentence-text"> course, I want to be able to:</span>
        </div>

        <div className="input-section">
          <textarea
            className="goal-input"
            placeholder="give a presentation at work"
            value={firstField}
            onChange={(e) => setFirstField(e.target.value)}
            disabled={isInvalidToken}
            rows={2}
          />
          <SuggestionChips
            suggestions={firstFieldSuggestions}
            onSelect={setFirstField}
            showMore={showMoreFirst}
            onShowMore={() => setShowMoreFirst(true)}
          />
        </div>

        <div className="goal-sentence">
          <span className="sentence-text">So that I can:</span>
        </div>

        <div className="input-section">
          <textarea
            className="goal-input"
            placeholder="get promoted at work"
            value={secondField}
            onChange={(e) => setSecondField(e.target.value)}
            disabled={isInvalidToken}
            rows={2}
          />
          <SuggestionChips
            suggestions={secondFieldSuggestions}
            onSelect={setSecondField}
            showMore={showMoreSecond}
            onShowMore={() => setShowMoreSecond(true)}
          />
        </div>

        <button className={`continue-btn ${canProceed ? 'active' : 'inactive'}`} onClick={handleSubmit} disabled={!canProceed || isInvalidToken}>
          Confirm My Goals
        </button>
      </div>

      <style jsx>{`
        .goal-setting-wrapper {
          width: 100%;
          max-width: 700px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .title {
          font-size: 2.5rem;
          font-weight: 700;
          text-align: center;
          color: rgb(51, 51, 51);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .subtitle {
          font-size: 1.1rem;
          text-align: center;
          color: rgb(102, 102, 102);
          margin-bottom: 1rem;
          line-height: 1.6;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .goal-builder {
          padding-left: 3rem;
          padding-right: 3rem;
        }

        .goal-sentence {
          margin: 1.5rem 0 1rem 0;
          font-size: 1.25rem;
          line-height: 1.6;
          text-align: center;
        }

        .sentence-text {
          color: rgb(68, 68, 68);
          font-weight: 500;
        }

        .language-highlight {
          background: linear-gradient(135deg, #40dba7 0%, #3fdba8 100%);
          color: #333;
          padding: 0.2rem 0.6rem;
          border-radius: 12px;
          font-weight: 600;
          margin: 0 0.3rem;
          display: inline-block;
          transform: translateY(-1px);
        }

        .input-section {
          margin: 2rem 0;
        }

        .goal-input {
          width: 100%;
          min-height: 60px;
          padding: 1rem 1.25rem;
          border: 2px solid rgb(217, 217, 217);
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 500;
          color: rgb(51, 51, 51);
          background: white;
          resize: none;
          font-family: inherit;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          line-height: 1.5;
          box-sizing: border-box;
          max-width: 100%;
        }

        .goal-input:focus {
          outline: none;
          border-color: #40dba7;
          box-shadow: 0 0 0 3px rgba(64, 219, 167, 0.1);
          transform: translateY(-1px);
        }

        .goal-input::placeholder {
          color: rgb(153, 153, 153);
          font-style: italic;
        }

        .goal-input:disabled {
          background-color: rgb(245, 245, 245);
          color: rgb(153, 153, 153);
          cursor: not-allowed;
        }

        .suggestions-container {
          margin-top: 1rem;
        }

        .chips-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .suggestion-chip {
          background: white;
          border: 2px solid rgb(217, 217, 217);
          border-radius: 12px;
          padding: 0.6rem 1rem;
          font-size: 0.9rem;
          font-weight: 500;
          color: rgb(68, 68, 68);
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          transform: translateY(0);
        }

        .suggestion-chip:hover:not(:disabled) {
          background: #40dba7;
          color: #333;
          border-color: #40dba7;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(64, 219, 167, 0.25);
        }

        .suggestion-chip:active:not(:disabled) {
          transform: translateY(0);
        }

        .suggestion-chip:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .show-more-btn {
          background: none;
          border: 2px dashed rgb(192, 192, 192);
          border-radius: 12px;
          padding: 0.6rem 1rem;
          font-size: 0.9rem;
          font-weight: 500;
          color: rgb(102, 102, 102);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .show-more-btn:hover:not(:disabled) {
          border-color: #40dba7;
          color: #40dba7;
        }

        .continue-btn {
          width: 100%;
          padding: 1rem 2rem;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          margin-top: 2rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(0);
        }

        .continue-btn.active {
          background: linear-gradient(135deg, #40dba7 0%, #3fdba8 100%);
          color: #333;
          box-shadow: 0 4px 16px rgba(64, 219, 167, 0.3);
        }

        .continue-btn.active:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(64, 219, 167, 0.4);
        }

        .continue-btn.inactive {
          background: rgb(217, 217, 217);
          color: rgb(153, 153, 153);
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .goal-setting-wrapper {
            padding: 0 1rem;
          }

          .title {
            font-size: 2rem;
          }

          .goal-builder {
            padding: 2rem 1.5rem;
            border-radius: 20px;
          }

          .goal-sentence {
            font-size: 1.1rem;
            margin: 1rem 0 0.75rem 0;
          }

          .chips-grid {
            gap: 0.5rem;
          }

          .suggestion-chip {
            font-size: 0.85rem;
            padding: 0.5rem 0.8rem;
          }

          .goal-input {
            font-size: 1rem;
            padding: 0.875rem 1rem;
          }
        }

        @media (max-width: 480px) {
          .chips-grid {
            flex-direction: column;
          }

          .suggestion-chip {
            text-align: center;
            white-space: normal;
          }
        }
      `}</style>
    </div>
  );
};

GoalSetting.propTypes = {
  initialInformation: PropTypes.object.isRequired,
  isInvalidToken: PropTypes.bool,
  moveToNextStep: PropTypes.func.isRequired,
  postGoalSetting: PropTypes.func.isRequired // Changed from dispatch to postGoalSetting
};

const mapStateToProps = (state) => ({
  initialInformation: state.newOnboarding.initialInformation
});

export default connect(mapStateToProps, { postGoalSetting })(GoalSetting);
